/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * Reads FIT files.
 *
 * ```kotlin
 * val result = FitDecoder(bytes).decode()
 * result.errors.forEach { println(it) }
 * result.messages.recordMesgs.forEach { println(it.heartRate) }
 * ```
 *
 * [decode] never throws on malformed input: it returns everything that decoded
 * before the problem alongside a description of it, because a truncated file
 * usually still holds most of an activity. Neither does [read], which reports
 * through its return value. Only [asSequence] throws, having no place to carry
 * an error.
 *
 * "Never throws" means no [Exception] escapes. A JVM [Error] — an
 * `OutOfMemoryError`, a `StackOverflowError` — does propagate: those say the
 * runtime is out of resources, not that the file is malformed, and swallowing
 * one into [DecodeResult.errors] would report a broken process as a corrupt
 * activity while leaving the caller to run on in an unusable VM.
 */
public class FitDecoder(
    private val bytes: ByteArray,
) {
    /** True when the input opens with a plausible FIT header. */
    public fun isFit(): Boolean = isFit(bytes)

    /**
     * True when every chained file's header CRC and file CRC check out.
     *
     * The header CRC lives in the 14-byte header form only, and even there a
     * producer may leave it at 0x0000 to mean "not computed", which the protocol
     * allows and this accepts. Anything else has to match the CRC over the first
     * 12 bytes. The 12-byte header form carries no header CRC at all.
     */
    public fun checkIntegrity(): Boolean {
        if (!isFit()) return false
        return try {
            if (!headerCrcsAreValid()) return false
            val cursor = MesgCursor(ByteReader(bytes), DecodeOptions(mode = DecodeMode.NORMAL))
            while (cursor.hasNext()) cursor.next()
            true
        } catch (_: FitException) {
            false
        } catch (_: Exception) {
            // Same safety net as decode(): an integrity check is a question, not
            // an operation that may fail. An Error still propagates.
            false
        }
    }

    /**
     * Checks the header CRC of every file in the stream.
     *
     * Walks the chain by itself rather than going through [MesgCursor], which
     * reports only what it reads past the header. A length that does not add up
     * ends the walk without a verdict: the record loop in [checkIntegrity] is
     * what reports a truncated or overlong file, and reporting it twice would
     * hide the more precise message.
     */
    private fun headerCrcsAreValid(): Boolean {
        var offset = 0
        while (offset < bytes.size) {
            if (bytes.size - offset < Fit.HEADER_WITHOUT_CRC_SIZE) return false

            val header = FileHeader.read(ByteReader(bytes, offset))
            if (!header.isValid) return false

            // 0x0000 is how a producer says "header CRC not filled in".
            if (header.hasCrc && header.headerCrc != UNSET_HEADER_CRC) {
                val computed = Crc.calculate(bytes, offset, offset + Fit.HEADER_WITHOUT_CRC_SIZE)
                if (computed != header.headerCrc) return false
            }

            val next = offset + header.headerSize + header.dataSize.toInt() + Fit.CRC_SIZE
            if (next <= offset || next > bytes.size) return true
            offset = next
        }
        return true
    }

    /**
     * Decodes the whole input.
     *
     * Never throws an [Exception]: problems come back in [DecodeResult.errors].
     * A JVM [Error] propagates — see the class documentation.
     */
    public fun decode(options: DecodeOptions = DecodeOptions()): DecodeResult {
        val messages = FitMessages()
        val ordered = mutableListOf<Mesg>()
        val errors = mutableListOf<FitError>()
        var profileVersion: UShort? = null

        val cursor = MesgCursor(ByteReader(bytes), options)
        try {
            while (cursor.hasNext()) {
                val mesg = cursor.next()
                ordered.add(mesg)
                messages.add(mesg)
            }
        } catch (e: FitFormatException) {
            errors.add(FitError(e))
        } catch (e: FitException) {
            errors.add(FitError(e.message ?: "decode error", -1, e))
        } catch (e: Exception) {
            // Last-resort net. Malformed input is supposed to surface as a
            // FitFormatException, but decode() promises never to throw, so an
            // unforeseen failure becomes an error entry rather than escaping.
            // Deliberately Exception and not Throwable: an Error means the VM is
            // in trouble, which is not something a FitError can describe.
            errors.add(FitError(unexpectedMessage(e), -1, e))
        } finally {
            profileVersion = cursor.profileVersion
            messages.addDeveloperFieldDescriptions(cursor.developerData.fieldDescriptions)
        }

        if (options.mergeHeartRates && options.expandComponents) {
            HrMesgUtil.mergeHeartRates(messages.hrMesgs, messages.recordMesgs)
        }

        return DecodeResult(messages, ordered, profileVersion, errors)
    }

    /**
     * Decodes lazily, handing each message to [onMesg] as it is read.
     *
     * Nothing is retained, so this is the entry point for files too large to
     * hold decoded in memory. Returns the errors that stopped the decode, if
     * any: like [decode], it never throws an [Exception] on malformed input. An
     * exception raised by [onMesg] itself does propagate, and so does an
     * [Error] from either side.
     */
    public fun read(options: DecodeOptions = DecodeOptions(), onMesg: (Mesg) -> Unit): List<FitError> {
        val errors = mutableListOf<FitError>()
        val iterator = asSequence(options).iterator()
        while (true) {
            // Only the decode is guarded: an exception out of [onMesg] is the
            // caller's own and must not be swallowed as a file error.
            val mesg = try {
                if (!iterator.hasNext()) break
                iterator.next()
            } catch (e: FitFormatException) {
                errors.add(FitError(e))
                break
            } catch (e: FitException) {
                errors.add(FitError(e.message ?: "decode error", -1, e))
                break
            } catch (e: Exception) {
                // See decode(): anything unforeseen is reported, not thrown.
                errors.add(FitError(unexpectedMessage(e), -1, e))
                break
            }
            onMesg(mesg)
        }
        return errors
    }

    /**
     * The file's messages as a lazily decoded [Sequence].
     *
     * Throws [FitFormatException] mid-iteration on malformed input. Note that
     * [DecodeOptions.mergeHeartRates] cannot apply here: merging needs the whole
     * file, which is exactly what streaming avoids holding.
     */
    public fun asSequence(options: DecodeOptions = DecodeOptions()): Sequence<Mesg> =
        Sequence { MesgCursor(ByteReader(bytes), options) }

    public companion object {
        /** True when [bytes] opens with a plausible FIT header. */
        public fun isFit(bytes: ByteArray): Boolean {
            if (bytes.size < Fit.HEADER_WITHOUT_CRC_SIZE) return false
            return try {
                val header = FileHeader.read(ByteReader(bytes))
                header.isValid && bytes.size >= header.headerSize + header.dataSize.toInt()
            } catch (_: FitException) {
                false
            } catch (_: Exception) {
                false
            }
        }

        /** A 14-byte header whose CRC field is zero is one that never computed it. */
        private val UNSET_HEADER_CRC: UShort = 0u

        /** Describes an exception the decoder was not expecting to see at all. */
        private fun unexpectedMessage(e: Throwable): String {
            val kind = e::class.simpleName ?: "error"
            val detail = e.message
            return if (detail.isNullOrEmpty()) "unexpected $kind while decoding" else "unexpected $kind while decoding: $detail"
        }
    }
}
